import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Archive,
  BadgeDollarSign,
  CalendarRange,
  Clock,
  Inbox,
  Kanban,
  Plus,
  Search,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/Button'
import { Badge } from '@/shared/components/ui/Badge'
import { Select } from '@/shared/components/ui/Select'
import { useAuth } from '@/context/AuthContext'
import { DealDetailModal, DealDetailData } from '@/components/sales/deal-detail-modal'
import { formatINR } from '@/shared/utils/currency'
import { ActivityTimeline } from '@/features/deals/timeline/ActivityTimeline'
import { AddActivityDialog, type ActivityKind } from '@/features/deals/timeline/AddActivityDialog'
import { DealsColumn } from '@/features/deals/timeline/DealsColumn'
import { DocumentsTabs } from '@/features/deals/timeline/DealTabs'
import { ClearFiltersChip, SideRail } from '@/features/deals/timeline/SideRail'
import { useDealTimeline, useDealTimelineDetail, useSalesEmployees } from '@/features/deals/timeline/api'
import { EMPTY_FILTERS, type TimelineFilters } from '@/features/deals/timeline/types'

const TABS = [
  { value: 'timeline', label: 'Timeline' },
  { value: 'overview', label: 'Overview' },
  { value: 'quotations', label: 'Quotations' },
  { value: 'proforma', label: 'Proforma Invoices' },
  { value: 'payment', label: 'Payment Schedule' },
  { value: 'invoices', label: 'Tax Invoices' },
]

const NAV = [
  { href: '/app/deals', label: 'Active Deals', icon: BadgeDollarSign },
  { href: '/app/deals/pipeline', label: 'Pipeline', icon: Kanban },
  { href: '/app/deals/timeline', label: 'Timeline', icon: Clock },
  { href: '/app/deals/archive', label: 'Archive', icon: Archive },
]

const STAGE_TONE: Record<string, string> = {
  discovery: 'warning',
  'demo & validation': 'purple',
  proposal: 'info',
  negotiation: 'primary',
  'closed won': 'success',
  'closed lost': 'danger',
}

function stageTone(stage: string) {
  return STAGE_TONE[String(stage || '').toLowerCase()] || 'neutral'
}

export default function DealsTimelinePage() {
  const { user } = useAuth()
  const [filters, setFilters] = useState<TimelineFilters>(EMPTY_FILTERS)
  const [activityType, setActivityType] = useState('all')
  const [tab, setTab] = useState('timeline')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dialogKind, setDialogKind] = useState<ActivityKind | null>(null)
  const [detailDeal, setDetailDeal] = useState<DealDetailData | null>(null)

  const list = useDealTimeline({ ...filters, scope: 'all' })
  const employees = useSalesEmployees()
  const deals = useMemo(() => list.data?.deals ?? [], [list.data])

  const scopedDeals = useMemo(() => {
    if (filters.scope === 'mine') return deals.filter((d) => d.assignedToUserId === user?.id)
    if (filters.scope === 'following') return deals.filter((d) => d.pinned)
    return deals
  }, [deals, filters.scope, user?.id])

  const counts = useMemo(
    () => ({
      all: deals.length,
      mine: deals.filter((d) => d.assignedToUserId === user?.id).length,
      following: deals.filter((d) => d.pinned).length,
    }),
    [deals, user?.id]
  )

  const activeId = scopedDeals.some((d) => d.id === selectedId) ? selectedId : scopedDeals[0]?.id ?? null
  const selectedDeal = scopedDeals.find((d) => d.id === activeId) || null
  const detail = useDealTimelineDetail(activeId, tab === 'timeline' ? activityType : 'all')

  useEffect(() => {
    if (!activeId) setSelectedId(null)
  }, [activeId])

  const patch = (next: Partial<TimelineFilters>) => setFilters((prev) => ({ ...prev, ...next }))
  const activeChips = [
    filters.stage && { label: filters.stage, clear: () => patch({ stage: '' }) },
    filters.employee && {
      label: (employees.data ?? []).find((e) => e.id === filters.employee)?.name || 'Employee',
      clear: () => patch({ employee: '' }),
    },
    (filters.from || filters.to) && {
      label: `${filters.from || '…'} → ${filters.to || '…'}`,
      clear: () => patch({ from: '', to: '' }),
    },
    filters.outcome !== 'all' && { label: filters.outcome, clear: () => patch({ outcome: 'all' }) },
    filters.search && { label: `“${filters.search}”`, clear: () => patch({ search: '' }) },
  ].filter(Boolean) as Array<{ label: string; clear: () => void }>

  const stageOptions = [
    { value: '', label: 'All Stages' },
    ...(list.data?.facets.stages ?? []).map((s) => ({ value: s.name, label: s.name })),
  ]
  const employeeOptions = [
    { value: '', label: 'All Employees' },
    ...(employees.data ?? []).map((e) => ({ value: e.id, label: e.name })),
  ]

  const stat = (key: 'all' | 'open' | 'won' | 'lost') =>
    patch({ outcome: filters.outcome === key ? 'all' : key })

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Deals Timeline</h1>
          <p className="text-sm text-muted-foreground">Track all deal activities across your team in one place.</p>
        </div>
        <Button size="sm" onClick={() => setDialogKind('note')} disabled={!activeId}>
          <Plus className="mr-1.5 h-4 w-4" /> Add Activity
        </Button>
      </div>

      <div className="flex items-center gap-2 border-b border-border/60 pb-3">
        {NAV.map((item) => {
          const Icon = item.icon
          const active = item.href === '/app/deals/timeline'
          return (
            <Link
              key={item.href}
              to={item.href}
              className={
                active
                  ? 'flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm'
                  : 'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
              }
            >
              <Icon className="h-3.5 w-3.5" /> {item.label}
            </Link>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={filters.search}
            onChange={(e) => patch({ search: e.target.value })}
            placeholder="Search deals, customers…"
            className="w-full rounded-xl border border-border/70 bg-card py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="w-[200px]">
          <Select value={filters.employee} onValueChange={(v) => patch({ employee: v })} options={employeeOptions} />
        </div>
        <div className="w-[190px]">
          <Select value={filters.stage} onValueChange={(v) => patch({ stage: v })} options={stageOptions} />
        </div>
        <div className="flex items-center gap-1.5 rounded-xl border border-border/70 bg-card px-3 py-2">
          <CalendarRange className="h-4 w-4 text-muted-foreground" />
          <input
            type="date"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => patch({ from: e.target.value })}
            className="bg-transparent text-sm focus:outline-none"
            aria-label="From date"
          />
          <span className="text-muted-foreground">–</span>
          <input
            type="date"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => patch({ to: e.target.value })}
            className="bg-transparent text-sm focus:outline-none"
            aria-label="To date"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setFilters(EMPTY_FILTERS)
            setActivityType('all')
          }}
          className="text-sm font-semibold text-primary hover:underline"
        >
          Clear Filters
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { key: 'all' as const, label: 'Total Deals', value: list.data?.stats.total ?? 0, icon: Inbox, tone: 'bg-blue-100 text-blue-700' },
          { key: 'open' as const, label: 'In Progress', value: list.data?.stats.inProgress ?? 0, icon: Clock, tone: 'bg-sky-100 text-sky-700' },
          { key: 'won' as const, label: 'Won', value: list.data?.stats.won ?? 0, icon: ThumbsUp, tone: 'bg-emerald-100 text-emerald-700' },
          { key: 'lost' as const, label: 'Lost', value: list.data?.stats.lost ?? 0, icon: ThumbsDown, tone: 'bg-rose-100 text-rose-700' },
        ].map((card) => {
          const Icon = card.icon
          const active = filters.outcome === card.key
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => stat(card.key)}
              className={`flex items-center gap-3 rounded-2xl border bg-card px-4 py-3 text-left transition-colors ${
                active ? 'border-primary/60 ring-2 ring-primary/15' : 'border-border/60 hover:border-primary/40'
              }`}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${card.tone}`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-muted-foreground">{card.label}</span>
                <span className="block text-xl font-bold text-foreground">{card.value}</span>
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row items-stretch min-h-[calc(100vh-380px)]">
        <DealsColumn
          deals={scopedDeals}
          selectedId={activeId}
          onSelect={setSelectedId}
          scope={filters.scope}
          onScopeChange={(scope) => patch({ scope })}
          counts={counts}
          stageTone={stageTone}
          loading={list.isLoading}
        />

        <main className="min-w-0 flex-1 rounded-2xl border border-border/60 bg-card flex flex-col">
          {!selectedDeal ? (
            <div className="px-6 py-16 text-center">
              <p className="text-sm font-semibold text-foreground">No deal selected</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {list.isLoading ? 'Loading your deals…' : 'Pick a deal on the left to see its history.'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/50 px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-lg font-bold text-foreground">{selectedDeal.title}</h2>
                    <Badge tone={stageTone(selectedDeal.stage)} size="sm">
                      {selectedDeal.stage}
                    </Badge>
                  </div>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <span>{selectedDeal.client}</span>
                    <span aria-hidden>•</span>
                    <span className="font-semibold text-foreground">{formatINR(selectedDeal.value)}</span>
                    <span aria-hidden>•</span>
                    <span>Expected Close: {selectedDeal.closeDate || 'not set'}</span>
                    <span aria-hidden>•</span>
                    <span>Owner: {selectedDeal.owner || 'unassigned'}</span>
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDetailDeal({ ...(detail.data?.deal || {}), id: selectedDeal.id } as DealDetailData)}
                >
                  View Deal
                </Button>
              </div>

              <div className="flex gap-1 overflow-x-auto border-b border-border/50 px-4">
                {TABS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setTab(item.value)}
                    className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
                      tab === item.value
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="px-5 py-4">
                {activeChips.length > 0 && (
                  <div className="mb-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Filters
                    </span>
                    {activeChips.map((chip) => (
                      <ClearFiltersChip key={chip.label} label={chip.label} onClear={chip.clear} />
                    ))}
                  </div>
                )}
                {tab === 'timeline' ? (
                  <ActivityTimeline activities={detail.data?.activities ?? []} loading={detail.isLoading} />
                ) : detail.isLoading && !detail.data ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
                ) : (
                  <DocumentsTabs detail={detail.data} tab={tab} />
                )}
              </div>
            </>
          )}
        </main>

        <SideRail
          from={filters.from}
          to={filters.to}
          stage={filters.stage}
          employee={filters.employee}
          activityType={activityType}
          stageOptions={stageOptions}
          employeeOptions={employeeOptions}
          team={detail.data?.team ?? []}
          onChange={patch}
          onActivityTypeChange={setActivityType}
        />
      </div>

      {dialogKind && activeId && (
        <AddActivityDialog
          kind={dialogKind}
          onClose={() => setDialogKind(null)}
          dealId={activeId}
          dealTitle={selectedDeal?.title || ''}
          leadId={detail.data?.lead?.id ?? null}
          employees={employees.data ?? []}
          currentUserId={user?.id}
        />
      )}

      <DealDetailModal
        isOpen={!!detailDeal}
        onClose={() => setDetailDeal(null)}
        deal={detailDeal}
        onUpdate={() => list.refetch()}
        onDeleted={() => list.refetch()}
        currentSectionName="Timeline"
        currentSectionHref="/app/deals/timeline"
      />
    </div>
  )
}
